import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';
import { SUPER_ADMIN_API, SuperAdminAuth } from '../../features/super-admin/super-admin.api';

/**
 * Anexa o token à nossa API. Em 401 da loja, renova o access token pelo cookie (refresh) e repete a
 * requisição uma vez; se a sessão acabou, desloga.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const superAdmin = inject(SuperAdminAuth);
  // o token só vai para a nossa API, nunca para serviços externos (ex.: ViaCEP)
  const isOwnApi = req.url.startsWith(environment.baseUrl);
  // rotas do super admin usam o token próprio dele (o das lojas nunca vai para lá, e vice-versa)
  const isSuperAdminApi = req.url.startsWith(SUPER_ADMIN_API);
  const isAuthApi = req.url.startsWith(`${environment.apiUrl}/auth/`);

  const withToken = (r: HttpRequest<unknown>, token: string | null) =>
    token && isOwnApi && !isAuthApi ? r.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : r;

  const token = isSuperAdminApi ? superAdmin.token() : auth.token();

  return next(withToken(req, token)).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401 || !isOwnApi || isAuthApi) return throwError(() => err);
      if (isSuperAdminApi) {
        if (!req.url.includes('/auth/')) superAdmin.logout();
        return throwError(() => err);
      }
      // sem sessão nenhuma (ex.: página pública) não há o que renovar
      if (!auth.store()) return throwError(() => err);
      return auth.refresh().pipe(
        switchMap((fresh) => {
          if (!fresh) {
            auth.logout();
            return throwError(() => err);
          }
          return next(withToken(req, fresh));
        }),
      );
    }),
  );
};
